// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React from 'react';
import Modal from './Modal';
import Button from './Button';
import ProgressBar from './ProgressBar';
import './CourseModal.css';
import * as NavUtils from '../utils/game/NavigationUtils';

/**
 * CourseModal Component
 * Displays course calculation progress and confirmation
 */
const CourseModal = ({
  show,
  selectedSystem,
  courseProgress,
  selectedWarpFactor,
  navigationMode,
  antimatterFuel,
  getDefaultWarpFactor,
  getDisplayDistance,
  calculateTravelTime,
  onConfirm,
  onCancel,
  autoEngageCountdown,
  // New props for alternative destinations
  currentLocation,
  starSystems,
  distancePositions,
  onSelectAlternative
}) => {
  if (!show || !selectedSystem) return null;

  const currentWarp = selectedWarpFactor || getDefaultWarpFactor();
  const distance = getDisplayDistance(selectedSystem);
  const fuelNeeded = NavUtils.calculateFuelNeeded(distance, currentWarp);
  const canReach = fuelNeeded <= antimatterFuel;

  // Find alternative destinations if we can't reach the selected one
  const alternativeDestinations = !canReach && currentLocation && starSystems && distancePositions ? 
    starSystems
      .filter(system => system.id !== selectedSystem.id && system.id !== currentLocation.id)
      .map(system => {
        const systemDistance = NavUtils.calculateDistance(currentLocation, system, distancePositions);
        const optimalWarp = NavUtils.calculateOptimalWarp(systemDistance, antimatterFuel);
        const systemFuelNeeded = NavUtils.calculateFuelNeeded(systemDistance, optimalWarp);
        
        return {
          system,
          distance: systemDistance,
          optimalWarp,
          fuelNeeded: systemFuelNeeded,
          canReach: systemFuelNeeded <= antimatterFuel
        };
      })
      .filter(alt => alt.canReach)
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 3) // Show top 3 closest alternatives
    : [];

  const getWarpColor = () => {
    if (currentWarp >= 7) return '#ff4444';
    if (currentWarp >= 4) return '#ffaa00';
    return '#00ff88';
  };

  const getWarpSubtext = () => {
    if (!canReach) {
      return (
        <div className="warp-subtext warning">
          ⚠️ INSUFFICIENT FUEL ⚠️
        </div>
      );
    }
    
    if (navigationMode === 'AUTO' && !selectedWarpFactor) {
      return <div className="warp-subtext info">(AUTO-SELECTED)</div>;
    }
    
    if (navigationMode === 'EMERGENCY' && !selectedWarpFactor) {
      return <div className="warp-subtext emergency">(EMERGENCY DEFAULT)</div>;
    }
    
    if (navigationMode === 'EMERGENCY' && currentWarp >= 8) {
      return <div className="warp-subtext emergency">⚠️ DANGER ZONE ⚠️</div>;
    }
    
    return null;
  };

  return (
    <Modal
      isOpen={show && selectedSystem}
      onClose={onCancel}
      size="medium"
      closeOnBackdrop={true}
      closeOnEscape={true}
      showCloseButton={false}
      className="course-modal-custom"
    >
      {/* Animated border */}
      <div className="course-modal-border" />
      
      {/* Title */}
      <div className="course-modal-title">
          ◊ COURSE LAID IN ◊
        </div>
        
        {/* Course calculation progress */}
        <div className="course-progress-section">
          <div className="course-progress-label">
            {courseProgress < 100 ? 'CALCULATING COURSE...' : 'COURSE LOCKED IN'}
          </div>
          
          <ProgressBar 
            value={courseProgress}
            max={100}
            color="info"
            showLabel={true}
            animated={courseProgress < 100}
            size="medium"
          />
        </div>
        
        {/* Destination */}
        <div className="course-destination">
          DESTINATION: {selectedSystem.name}
        </div>
        
        {/* Distance */}
        <div className="course-distance">
          DISTANCE: {distance} LIGHT YEARS
        </div>
        
        {/* Fuel Cost Estimate */}
        <div className="course-fuel-cost">
          <div className="fuel-cost-label">FUEL REQUIRED:</div>
          <div className="fuel-cost-value" style={{ 
            color: canReach ? (fuelNeeded > antimatterFuel * 0.8 ? '#ffaa00' : '#00ff88') : '#ff4444'
          }}>
            {fuelNeeded}% 
            <span className="fuel-cost-current"> (Current: {Math.round(antimatterFuel)}%)</span>
          </div>
          {!canReach && (
            <div className="fuel-cost-warning">
              ⚠️ Need {fuelNeeded - Math.round(antimatterFuel)}% more fuel
            </div>
          )}
        </div>
        
        {/* Warp Factor */}
        <div className="course-warp" style={{ color: getWarpColor() }}>
          WARP {currentWarp}
          {getWarpSubtext()}
        </div>
        
        {/* ETA */}
        <div className="course-eta">
          ETA: {Math.round(distance / Math.pow(currentWarp, 3))} HOURS
        </div>
        
        {/* Status message */}
        <div
          className="course-status"
          style={{ color: navigationMode === 'EMERGENCY' ? '#ff4444' : (courseProgress >= 100 && autoEngageCountdown != null ? '#00ffff' : '#00ffff') }}
        >
          {navigationMode === 'EMERGENCY'
            ? '⚠️ WARNING: High-speed emergency maneuver detected!'
            : courseProgress >= 100 && autoEngageCountdown != null
              ? <span style={{ color: '#00ffff', animation: 'pulse 0.8s ease-in-out infinite' }}>⚡ ENGAGING IN {autoEngageCountdown}...</span>
              : '"Engage when ready, Captain."'
          }
        </div>
        
        {/* Action buttons for insufficient fuel or emergency mode */}
        {(!canReach || navigationMode === 'EMERGENCY') && (
          <div className="course-actions">
            {!canReach ? (
              <>
                <div className="fuel-alternatives">
                  <div className="alternatives-title">⚠️ INSUFFICIENT FUEL - OPTIONS:</div>
                  <div className="alternatives-options">
                    <Button 
                      variant="warning"
                      size="medium"
                      onClick={() => {
                        // Calculate optimal warp factor for available fuel
                        const optimalWarp = NavUtils.calculateOptimalWarp(distance, antimatterFuel);
                        const optimalFuelNeeded = NavUtils.calculateFuelNeeded(distance, optimalWarp);
                        
                        if (optimalFuelNeeded <= antimatterFuel) {
                          // Set the optimal warp factor and proceed
                          onConfirm(optimalWarp);
                        }
                      }}
                    >
                      🔧 REDUCE TO WARP {NavUtils.calculateOptimalWarp(distance, antimatterFuel)}
                      <div className="alternative-details">
                        Fuel needed: {NavUtils.calculateFuelNeeded(distance, NavUtils.calculateOptimalWarp(distance, antimatterFuel))}%
                      </div>
                    </Button>
                    <Button 
                      variant="danger"
                      size="medium"
                      onClick={() => onConfirm(currentWarp, true)} // Pass risky flag
                    >
                      ⚠️ PROCEED ANYWAY (RISKY)
                      <div className="risky-details">
                        Risk of getting stranded • Free tow available
                      </div>
                    </Button>
                    <Button 
                      variant="secondary"
                      size="medium"
                      onClick={onCancel}
                    >
                      ⛽ FIND FUEL STATION
                    </Button>
                  </div>
                  
                  {/* Alternative Destinations */}
                  {alternativeDestinations.length > 0 && (
                    <div className="alternative-destinations">
                      <div className="alternatives-subtitle">🎯 OR GO TO CLOSER DESTINATIONS:</div>
                      <div className="destination-options">
                        {alternativeDestinations.map((alt, index) => (
                          <Button
                            key={alt.system.id}
                            variant="success"
                            size="medium"
                            onClick={() => onSelectAlternative && onSelectAlternative(alt.system)}
                          >
                            🚀 {alt.system.name}
                            <div className="destination-details">
                              {alt.distance.toFixed(1)} LY • Warp {alt.optimalWarp} • {alt.fuelNeeded}% fuel
                            </div>
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="fuel-options-hint">
                    💡 Visit a station to refuel or choose a closer destination
                  </div>
                </div>
              </>
            ) : (
              <>
                <Button 
                  variant="danger"
                  size="large"
                  onClick={onConfirm}
                >
                  ⚠️ CONFIRM EMERGENCY COURSE
                </Button>
                <Button 
                  variant="secondary"
                  size="large"
                  onClick={onCancel}
                >
                  CANCEL
                </Button>
              </>
            )}
          </div>
        )}
    </Modal>
  );
};

export default CourseModal;
